import { useContext, useEffect, useRef, useState } from 'react';
import { Contract } from 'type/type';
import { decryptAES256, getContractList } from 'api/api';
import Pagenation from 'components/common/Pagenation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminLoginInfoContext, CommonTable, CommonTableWrap, FilterContentContainer } from 'pages/Layout';
import { PrimaryButton } from 'components/common/Button';
import { calculMonthDateToString, contractStatusLabel, formatDateToString } from 'pages/utils/commonUtils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

interface SearchInputType {
  contract_no: string,
  name: string,
  company_name: string,
  mobile: string,
  is_contain: string,
  start_date: string,
  end_date: string,
  jisa_code: string[],
  contract_status: string,
  page?: string,
};

const ExcelToJson = () => {
  const [contractData, setContractData] = useState<Contract[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  // 파일 선택 input 클릭 시
  const handleButtonClick = (e: any) => {
    fileInput.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);

    const worksheet = workbook.worksheets[0]; // 첫 번째 시트
    const rows: any[] = [];

    const header: string[] = [];

    worksheet.eachRow((row, rowNumber) => {
      const rowValues = row.values as any[];
      if (rowNumber === 1) {
        // 첫 줄: 헤더
        rowValues.forEach((val, i) => {
          if (i > 0) header.push(val);
        });
      } else {
        // 그 외: 데이터
        const obj: any = {};
        rowValues.forEach((val, i) => {
          if (i > 0) obj[header[i - 1]] = val;
        });
        rows.push(obj);
      }
    });
    setContractData(rows);
  };

  return (
    <>
      <PrimaryButton type="button" onClick={handleButtonClick}>Excel 등록</PrimaryButton>
      <input type="file" name="file" id="file_name" onChange={handleFileUpload} accept='.xlsx' style={{ display: "none" }} ref={fileInput} />
    </>
  );
};

function ContractList() {
  const isFirstRender = useRef(true);
  const { adminLoginInfo } = useContext(AdminLoginInfoContext);
  const [searchParams, setSearchParams] = useSearchParams();
  // 필터obj
  const [searchObj, setSearchObj] = useState<SearchInputType>({
    is_contain: "1",
    contract_no: searchParams.get("contract_no") || "",
    name: searchParams.get("name") || "",
    company_name: searchParams.get("company_name") || "",
    mobile: searchParams.get("mobile") || "",
    start_date: searchParams.get("start_date") || calculMonthDateToString(new Date(), -3),
    end_date: searchParams.get("end_date") || formatDateToString(new Date()),
    page: searchParams.get("page") ? String(searchParams.get("page")) : undefined,
    jisa_code: Array.isArray(adminLoginInfo.jisa_code)
      ? (adminLoginInfo.jisa_code as string[])
      : typeof adminLoginInfo.jisa_code === "string"
      ? [adminLoginInfo.jisa_code] // 문자열이면 배열로 변환
      : [],
    contract_status: searchParams.get("contract_status") || ""
  });
  const [excelObj, setExcelObj] = useState<SearchInputType|null>(null);

  const navigate = useNavigate();
  const [contractList, setContractList] = useState<Contract[]>([]);
  // 페이지네이션
  const buttonPerPage = 10; // 보여줄 페이지네이션 버튼 수
  const [page, setPage] = useState(searchObj.page ? Number(searchObj.page) : 1); // 현재 페이지
  const [limit] = useState(10); // 보여줄 게시물 개수
  const [totalCount, setTotalCount] = useState(-1);
  const numPages = Math.ceil(totalCount / limit); // 총 페이지 수
  const [startButtonNum, setStartButtonNum] = useState(1); // 버튼 시작 숫자

  const REACT_APP_SECRET_KEY = process.env.REACT_APP_SECRET_KEY!;
  const REACT_APP_SECRET_IV = process.env.REACT_APP_SECRET_IV!;

  // 페이지네이션 버튼 클릭 시
  const isClickButton = (str:string) => {
    if (str === 'prev') handlePrevPage();
    else handleNextPage();
  };

  // 이전페이지 이동(buttonPerPage만큼 -)
  const handlePrevPage = () => {
    const prevPage = Math.max(startButtonNum - buttonPerPage, 1);
    setStartButtonNum(prevPage);
    setPage(prevPage);
  };

  // 다음페이지 이동(buttonPerPage만큼 +)
  const handleNextPage = () => {
    const nextPage = Math.min(startButtonNum + buttonPerPage, numPages);
    setStartButtonNum(nextPage);
    setPage(nextPage);
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') searchHandle();
  }

  const searchHandle = () => {
    page === 1 ? getContracts(page, limit) : setPage(1);
  }

  const getContracts = async (page: number, limit: number) => {
    const { contract_no, name, company_name, mobile, start_date, end_date, is_contain, jisa_code, contract_status } = searchObj;
    const contractRes = await getContractList(page, limit, searchObj);
    if (contractRes.code === 200) {
      setContractList(contractRes.data);
      setTotalCount(contractRes.total_count);
    }
    setExcelObj(searchObj);
    setSearchParams({ contract_no, name, company_name, mobile, start_date, end_date, is_contain, jisa_code, contract_status, page: String(page) });
  };
  // 검색 value 변경
  const handleSearchValue = (e: any) => {
    const { name, value } = e.target;
    setSearchObj(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // 검색 날짜 변경
  const handleSearchDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "search_start_date") setSearchObj(prev => ({ ...prev, start_date: value }));
    else setSearchObj(prev => ({...prev, end_date: value}));
  };

  // 엑셀 다운로드 버튼
  const handleExcelDownload = async () => {
    try {
      if (!excelObj) return alert('먼저 조회 후 Excel 다운을 해주세요.');
  
      // 1. 데이터 조회
      const { code, data } = await getContractList(0, 0, excelObj);
      if (code !== 200 || !data) {
        return alert("Excel 변환에 실패하였습니다.");
      }
  
      const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;
      const SECRET_IV = process.env.REACT_APP_SECRET_IV;
  
      if (!SECRET_KEY || !SECRET_IV) {
        alert("암호화 키가 설정되지 않았습니다.");
        return;
      }
  
      // 2. 데이터 가공
      const formattedData = data.map((contract: Contract, idx: number) => {
        return {
          "No": idx + 1,
          "계약번호": contract.contract_no,
          "매칭 ID": contract.user_id || "",
          "계약자명": contract.name,
          "연락처": decryptAES256(contract.mobile, SECRET_KEY, SECRET_IV),
          "주소": `${contract.address}, ${contract.address_detail}`,
          "상호명": contract.company_name,
          "사업자번호": contract.business_number,
          "업태": contract.business_type,
          "종목": contract.business_items,
          "계약상태": contractStatusLabel.find(item => item.code === contract.contract_status)?.name || "",
          "계약상품": contract.contract_product,
          "영업지사": contract.jisa_name,
          "영업자": contract.sales?.name ?? contract.sales_id,
          "월/요금": contract.price,
          "계약시작일자": contract.start_date,
          "계약종료일자": contract.end_date,
        };
      });
  
      // 3. 엑셀 생성
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Contract Data');
  
      worksheet.columns = [
        { header: 'No', key: 'No', width: 5 },
        { header: '계약번호', key: '계약번호', width: 20 },
        { header: '매칭 ID', key: '매칭 ID', width: 15 },
        { header: '계약자명', key: '계약자명', width: 15 },
        { header: '연락처', key: '연락처', width: 15 },
        { header: '주소', key: '주소', width: 50 },
        { header: '상호명', key: '상호명', width: 20 },
        { header: '사업자번호', key: '사업자번호', width: 20 },
        { header: '업태', key: '업태', width: 10 },
        { header: '종목', key: '종목', width: 10 },
        { header: '계약상태', key: '계약상태', width: 10 },
        { header: '계약상품', key: '계약상품', width: 10 },
        { header: '영업지사', key: '영업지사', width: 10 },
        { header: '영업자', key: '영업자', width: 10 },
        { header: '월/요금', key: '월/요금', width: 15, style: { numFmt: '#,##0' } },
        { header: '계약시작일자', key: '계약시작일자', width: 15 },
        { header: '계약종료일자', key: '계약종료일자', width: 15 },
      ];
  
      worksheet.addRows(formattedData);
  
      // 4. 엑셀 다운로드
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, 'contract_data.xlsx');
    } catch (error) {
      console.error('Excel 다운로드 중 오류 발생:', error);
      alert("엑셀 다운로드 중 오류가 발생했습니다.");
    }
  };
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const hasParams = searchParams.toString() !== "";
      if (!hasParams) return; // ❌ 파라미터 없으면 첫 렌더 스킵
    }
    getContracts(page, limit); // ✅ 이후에는 무조건 실행
  }, [page, limit]);
  return (
    <div className="notranslate" translate="no" suppressHydrationWarning={true}>
      <FilterContentContainer>
        <label>계약상태</label>
        <div style={{ minWidth: '5rem' }}>
          <select name="contract_status" onChange={handleSearchValue} value={searchObj.contract_status}>
            <option value="">전체</option>
            {contractStatusLabel.map((item) => (
              <option key={`contract-status-${item.code}`} value={item.code}>{item.name}</option>
            ))}
          </select>
        </div>
        <div className='flex-row'>
          <input type="text" name="contract_no" onChange={handleSearchValue} value={searchObj.contract_no} placeholder="계약번호 입력" onKeyUp={handleKeyDown} style={{ width: "10rem" }} />
          <input type="text" name="name" onChange={handleSearchValue} value={searchObj.name} placeholder="계약자명 입력" onKeyUp={handleKeyDown} style={{ width: "10rem" }} />
          <input type="text" name="company_name" onChange={handleSearchValue} value={searchObj.company_name} placeholder="상호명 입력" onKeyUp={handleKeyDown} style={{ width: "10rem" }} />
          <input type="text" name="mobile" onChange={handleSearchValue} value={searchObj.mobile} placeholder="연락처 입력" onKeyUp={handleKeyDown} style={{ width: "10rem" }} />
          <label>계약일</label>
          <div className='flex-row'>
            <input type='date' name='search_start_date' value={searchObj.start_date} onChange={handleSearchDate} />
            <span>~</span>
            <input type='date' name='search_end_date' value={searchObj.end_date} onChange={handleSearchDate} />
          </div>
          <div className='flex-row btn-wrap'>
            <PrimaryButton type="button" onClick={searchHandle}>조회</PrimaryButton>
            <PrimaryButton type="button" onClick={handleExcelDownload}>Excel 다운</PrimaryButton>
          </div>
        </div>
        <div className='flex-row btn-wrap'>
          {/* <ExcelToJson /> */}
          <PrimaryButton type="button" onClick={() => navigate('/admin/contract/register', { state: excelObj })}>등록</PrimaryButton>
        </div>
      </FilterContentContainer>
      <div>
        검색 결과: <span style={{ fontWeight: "600" }}>{totalCount < 0 ? 0 : totalCount}</span>건
      </div>
      <CommonTableWrap>
        <CommonTable>
          <thead style={{ fontSize: "14px" }}>
            <tr>
              <th>No</th>
              <th>계약번호</th>
              <th>매칭 ID</th>
              <th>계약자명</th>
              <th>연락처</th>
              <th>주소</th>
              <th>상호명</th>
              <th>사업자번호</th>
              <th>업태</th>
              <th>종목</th>
              <th>계약상태</th>
              <th>계약상품</th>
              <th>영업지사</th>
              <th>영업자</th>
              <th>월/요금</th>
              <th>계약일자</th>
              <th>계약시작일자</th>
              <th>계약종료일자</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: "14px" }}>
            {totalCount === 0 && (
              <tr>
                <td colSpan={18}>검색결과가 없습니다.</td>
              </tr>
            )}
            {totalCount > 0 && (
              contractList.map((contract, idx) => 
                <tr key={`contract_${contract.contract_no}_${idx}`} onClick={() => navigate(`/admin/contract/edit/${contract.contract_no}`, { state : excelObj })}>
                  <td><span>{(page - 1) * 10 + idx + 1}</span></td>
                  <td><span>{contract.contract_no}</span></td>
                  <td><span>{contract.user_id}</span></td>
                  <td><span>{contract.name}</span></td>
                  <td><span>{decryptAES256(contract.mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!)}</span></td>
                  <td><span>{`${contract.address.split(' ')[0]} ${contract.address.split(' ')[1]}`}</span></td>
                  <td><span>{contract.company_name}</span></td>
                  <td><span>{contract.business_number}</span></td>
                  <td><span>{contract.business_type}</span></td>
                  <td><span>{contract.business_items}</span></td>
                  <td style={{ color: contract.contract_status === "G" ? "rgb(255 189 104)" : "" }}>
                    <span>{contractStatusLabel.find(item => item.code === contract.contract_status)?.name || ""}</span>
                  </td>
                  <td><span>{contract.contract_product}</span></td>
                  <td><span>{contract.jisa_name}</span></td>
                  <td><span>{contract.sales?.name ?? contract.sales_id}</span></td>
                  <td><span>{contract.price.toLocaleString("ko-KR")}</span></td>
                  <td><span>{contract.contract_date}</span></td>
                  <td><span>{contract.start_date}</span></td>
                  <td><span>{contract.end_date}</span></td>
                </tr>
              )
            )}
          </tbody>
        </CommonTable>
      </CommonTableWrap>
      <Pagenation
        totalCount={totalCount}
        total={numPages}
        limit={limit}
        page={page}
        setPage={setPage}
        startButtonNum={startButtonNum}
        setClickButton={isClickButton}
      />
    </div>
  );
}

export default ContractList;